// context/IEPContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import IEPEngine from '../utils/iepEngine';
import IEPMasterData from '../data/IEPMasterData_Final.json';

const IEPContext = createContext();

export const useIEP = () => useContext(IEPContext);

export const IEPProvider = ({ children }) => {
  const [iepEngine, setIepEngine] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [transformations, setTransformations] = useState({});
  const [dimensions, setDimensions] = useState({});
  const [recommendations, setRecommendations] = useState([]);
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maturityScore, setMaturityScore] = useState(null);

  // Initialize IEP Engine
  useEffect(() => {
    const engine = new IEPEngine(IEPMasterData);
    setIepEngine(engine);
    setTransformations(IEPMasterData.transformations);
    setDimensions(IEPMasterData.dimensions);
    
    // Load saved organization
    const savedOrg = localStorage.getItem('iep_organization');
    if (savedOrg) {
      const orgData = JSON.parse(savedOrg);
      setOrganization(orgData);
      
      // Calculate initial recommendations
      const recs = engine.getRecommendedTransformations(orgData, 10);
      setRecommendations(recs);
      
      // Calculate maturity score
      const score = engine.calculateMaturityScore(orgData);
      setMaturityScore(score);
      
      // Generate initial roadmap
      const roadmap = engine.generateRoadmap(orgData, '1y');
      setRoadmap(roadmap);
    }
    
    setLoading(false);
  }, []);

  // Update organization and recalculate everything
  const updateOrganization = (orgData) => {
    setOrganization(orgData);
    localStorage.setItem('iep_organization', JSON.stringify(orgData));
    
    if (iepEngine) {
      const recs = iepEngine.getRecommendedTransformations(orgData, 10);
      setRecommendations(recs);
      
      const score = iepEngine.calculateMaturityScore(orgData);
      setMaturityScore(score);
      
      const roadmap = iepEngine.generateRoadmap(orgData, '1y');
      setRoadmap(roadmap);
    }
  };

  // Run simulation
  const runSimulation = (scenario, transformations) => {
    if (!iepEngine || !organization) return null;
    return iepEngine.simulateImpact(transformations, scenario, organization);
  };

  // Get transformation by ID
  const getTransformation = (id) => {
    return transformations[id] || null;
  };

  // Get dimension by ID
  const getDimension = (id) => {
    return dimensions[id] || null;
  };

  // Optimize sequence with Q*
  const optimizeSequence = (transformationsList, constraints) => {
    if (!iepEngine) return null;
    return iepEngine.qStarOptimizeSequence(transformationsList, constraints);
  };

  // Calculate dimension improvement
  const calculateDimensionImprovement = (dimensionId, transformationId) => {
    if (!iepEngine || !organization) return 0;
    return iepEngine.calculateDimensionImpact(dimensionId, transformationId, organization);
  };

  // Generate custom roadmap
  const generateCustomRoadmap = (selectedTransformations, timeframe) => {
    if (!iepEngine || !organization) return null;
    return iepEngine.generateCustomRoadmap(selectedTransformations, timeframe, organization);
  };
  
  // Add transformation to roadmap
const addToRoadmap = (transformation) => {
  if (!organization || !roadmap) return null;
  
  // Create updated roadmap
  const updatedRoadmap = { ...roadmap };
  
  // Add to first phase by default, or create new phase
  if (!updatedRoadmap.phases[0].transformations) {
    updatedRoadmap.phases[0].transformations = [];
  }
  
  // Check if already exists
  const exists = updatedRoadmap.phases[0].transformations.some(
    t => t.id === transformation.id
  );
  
  if (!exists) {
    updatedRoadmap.phases[0].transformations.push({
      ...transformation,
      addedAt: new Date().toISOString(),
      status: 'pending',
      progress: 0
    });
    
    // Recalculate resource allocation
    updatedRoadmap.resourceAllocation = recalculateResources(updatedRoadmap);
    
    // Update state
    setRoadmap(updatedRoadmap);
    
    // Save to localStorage
    const updatedOrg = {
      ...organization,
      roadmap: updatedRoadmap
    };
    setOrganization(updatedOrg);
    localStorage.setItem('iep_organization', JSON.stringify(updatedOrg));
    
    return { success: true, message: 'Added to roadmap successfully!' };
  }
  
  return { success: false, message: 'Already in roadmap' };
};

// Remove from roadmap
const removeFromRoadmap = (transformationId) => {
  if (!organization || !roadmap) return null;
  
  const updatedRoadmap = { ...roadmap };
  
  // Remove from all phases
  updatedRoadmap.phases = updatedRoadmap.phases.map(phase => ({
    ...phase,
    transformations: phase.transformations?.filter(t => t.id !== transformationId) || []
  }));
  
  // Recalculate
  updatedRoadmap.resourceAllocation = recalculateResources(updatedRoadmap);
  
  // Update state
  setRoadmap(updatedRoadmap);
  
  // Save
  const updatedOrg = {
    ...organization,
    roadmap: updatedRoadmap
  };
  setOrganization(updatedOrg);
  localStorage.setItem('iep_organization', JSON.stringify(updatedOrg));
  
  return { success: true, message: 'Removed from roadmap' };
};

// Recalculate resources helper
const recalculateResources = (roadmapData) => {
  const totalBudget = roadmapData.phases.reduce((sum, phase) => {
    return sum + (phase.transformations?.reduce((phaseSum, t) => {
      return phaseSum + (t.cost || t.resourceRequirements?.budget?.min || 100000);
    }, 0) || 0);
  }, 0);
  
  const totalDuration = roadmapData.phases.reduce((sum, phase) => {
    return sum + (phase.transformations?.reduce((phaseSum, t) => {
      return phaseSum + (t.duration || t.expectedDuration?.min || 60);
    }, 0) || 0);
  }, 0);
  
  return {
    ...roadmapData.resourceAllocation,
    totalBudget,
    totalDuration,
    quarterlyBreakdown: roadmapData.phases.map((phase, index) => ({
      phase: phase.name,
      budget: phase.transformations?.reduce((sum, t) => 
        sum + (t.cost || t.resourceRequirements?.budget?.min || 100000), 0) || 0,
      teamSize: phase.transformations?.reduce((sum, t) => 
        sum + (t.resourceRequirements?.teamSize?.core || 3), 0) || 0
    }))
  };
};

// Check if transformation is in roadmap
const isInRoadmap = (transformationId) => {
  if (!roadmap) return false;
  
  return roadmap.phases.some(phase => 
    phase.transformations?.some(t => t.id === transformationId)
  );
};


  const value = {
    iepEngine,
    organization,
    transformations,
    dimensions,
    recommendations,
    roadmap,
    maturityScore,
    loading,
    updateOrganization,
	addToRoadmap,        // ✅ ADDED
    removeFromRoadmap,   // ✅ ADDED
    isInRoadmap,         // ✅ ADDED
    runSimulation,
    getTransformation,
    getDimension,
    optimizeSequence,
    calculateDimensionImprovement,
    generateCustomRoadmap
  };

  return <IEPContext.Provider value={value}>{children}</IEPContext.Provider>;
};

export default IEPContext;